import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Gift, Phone, AlertTriangle, LogOut, MoreHorizontal, Moon, Sun, Receipt, Building, ShoppingBag, Store, Truck, Star } from 'lucide-react';
import { logout, getUser } from '../state/authSlice';
import { getIsPartnerLinked, getCourierSetting } from '../state/courierSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useTrackEvent } from '../utils/track';

const More = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const track = useTrackEvent();
  const user = useSelector(getUser);
  const isPartnerLinked = useSelector(getIsPartnerLinked);
  const courierSetting = useSelector(getCourierSetting);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const handleSignOut = () => {
    dispatch(logout());
    navigate('/');
  };

  const toggleDarkMode = () => {
    track('dark_mode_toggled', { enabled: !isDarkMode });
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Icon in circle */}
      <div className="flex justify-center mt-8 mb-4">
        <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center">
          <MoreHorizontal className="h-8 w-8 text-white" />
        </div>
      </div>
      {/* Menu Items */}
      <div className="bg-white dark:bg-gray-800 rounded-lg mx-4 shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        {!user?.isCourier && (
          <Link to="/profile" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <User className="h-6 w-6 text-primary dark:text-blue-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Profile
            </span>
          </Link>
        )}
        {!user?.isCourier && (
          <Link to="/referrals" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <Gift className="h-6 w-6 text-pink-500 dark:text-pink-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Referrals & Rewards
            </span>
          </Link>
        )}
        {!user?.isCourier && (
          <Link to="/authorized-users" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <User className="h-6 w-6 text-blue-500 dark:text-blue-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Authorized Users
            </span>
          </Link>
        )}
        {courierSetting?.isShopForMeEnabled === true && !user?.isCourier && (
          <Link to="/shop-for-me" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <ShoppingBag className="h-6 w-6 text-indigo-500 dark:text-indigo-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Shop For Me
            </span>
          </Link>
        )}
        {courierSetting?.isShopEnabled === true && !user?.isCourier && (
          <Link to="/shop" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <Store className="h-6 w-6 text-teal-500 dark:text-teal-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Shop
            </span>
          </Link>
        )}
        {courierSetting?.isLocalDeliveryEnabled === true && !user?.isCourier && (
          <Link to="/local-delivery" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <Truck className="h-6 w-6 text-emerald-500 dark:text-emerald-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Local Delivery
            </span>
          </Link>
        )}
        {isPartnerLinked && (
          <Link to="/invoices" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <Receipt className="h-6 w-6 text-purple-500 dark:text-purple-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Invoices
            </span>
          </Link>
        )}
        {user?.isCourier && isPartnerLinked && (
          <Link to="/warehouse" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <Building className="h-6 w-6 text-indigo-500 dark:text-indigo-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Warehouse
            </span>
          </Link>
        )}
        {courierSetting?.isReviewsEnabled === true && !user?.isCourier && (
          <Link to="/reviews" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700" data-testid="more-leave-review">
            <div className="w-8 h-8 mr-3 flex items-center justify-center">
              <Star className="h-6 w-6 text-amber-500 dark:text-amber-400" />
            </div>
            <span className="text-gray-800 dark:text-white font-medium">
              Leave a Review
            </span>
          </Link>
        )}
        <Link to="/contact-us" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-8 h-8 mr-3 flex items-center justify-center">
            <Phone className="h-6 w-6 text-green-500 dark:text-green-400" />
          </div>
          <span className="text-gray-800 dark:text-white font-medium">
            Contact Us
          </span>
        </Link>
        <Link to="/report-problem" className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-8 h-8 mr-3 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-orange-500 dark:text-orange-400" />
          </div>
          <span className="text-gray-800 dark:text-white font-medium">
            Report a Problem
          </span>
        </Link>
        <button 
          onClick={toggleDarkMode}
          className="w-full flex items-center p-4 border-b border-gray-200 dark:border-gray-700"
        >
          <div className="w-8 h-8 mr-3 flex items-center justify-center">
            {isDarkMode ? (
              <Sun className="h-6 w-6 text-yellow-500 dark:text-yellow-400" />
            ) : (
              <Moon className="h-6 w-6 text-purple-500 dark:text-purple-400" />
            )}
          </div>
          <span className="text-gray-800 dark:text-white font-medium">
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </span>
        </button>
      </div>
      {/* Sign Out Button */}
      <div className="px-4 mt-6">
        <button onClick={handleSignOut} className="w-full bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-md flex items-center justify-center shadow-sm">
          <LogOut className="h-5 w-5 mr-2" />
          SIGN OUT
        </button>
      </div>
    </div>
  );
};

export default More;
